import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { Carousel } from '@shared/components/carousel/Carousel';
import { Icon } from '@shared/components/icon/Icon';
import SkeletonPlaceholder from '@shared/components/skeletonPlaceholder/SkeletonPlaceholder';
import type { RestaurantEvent } from '@shared/types/restaurants';
import {
  toShortMonthNameDayFormat,
  toShortTimeOfDay,
} from '@shared/utils/dateFormatters';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import cx from 'classnames';
import type { SyntheticEvent } from 'react';
import typography from '~styles/typography.scss';
import styles from './RestaurantEventCard.scss';

export enum RestaurantEventStatus {
  Published = 'published',
}

export const bgGradient =
  'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.00) 50%, rgba(31, 31, 31, 0.30) 65%, rgba(31, 31, 31, 0.60) 75%, rgba(31, 31, 31, 0.85) 85%, rgba(31, 31, 31, 0.95) 92%, #1F1F1F 100%)';

export interface RestaurantEventCardContentProps {
  restaurantEvent?: RestaurantEvent | null;
  statusList?: RestaurantEventStatus[];
  showDescription?: boolean;
  action?: React.ReactNode;
  /** When true, header photos advance automatically every 3s (unless reduced motion). Default true. */
  headerImageCarouselAutoAdvance?: boolean;
}

export const RestaurantEventCardContent = ({
  restaurantEvent,
  statusList,
  showDescription = true,
  action,
  headerImageCarouselAutoAdvance = true,
}: RestaurantEventCardContentProps) => {
  const { name: restaurantName } = useRestaurant();
  const images = restaurantEvent?.images ?? [];
  const hasHeaderImages = images.length > 0;
  const hasEvent = Boolean(restaurantEvent?.id);

  const startTime = restaurantEvent?.shift?.startTime;
  const endTime = restaurantEvent?.shift?.endTime;
  const eventTime =
    startTime && endTime
      ? `${toShortTimeOfDay(startTime)} - ${toShortTimeOfDay(endTime)}`
      : '';

  const stopCardActivation = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <div className={styles.restaurantEventHeader}>
        {hasHeaderImages ? (
          <Carousel
            key={images.map((img) => `${img.id}:${img.imageKey}`).join('|')}
            variant="compact"
            className={styles.eventCardImageCarousel}
            slides={images.map((image) => ({
              key: image.id,
              backgroundImage: `${bgGradient}, url(${getRestaurantImagePath(image.imageKey)})`,
            }))}
            autoAdvance={headerImageCarouselAutoAdvance}
            onNavInteraction={stopCardActivation}
          />
        ) : (
          <div className={styles.imagePlaceholder} aria-hidden>
            <Icon
              name="imagePlaceholder"
              className={styles.imagePlaceholderIcon}
            />
          </div>
        )}
        <div className={styles.restaurantEventStatusHeader}>
          <h2 className={cx(typography.h3, styles.restaurantEventName)}>
            {restaurantEvent?.name != null ? (
              <span className={styles.restaurantEventNameText}>
                {restaurantEvent.name}
              </span>
            ) : (
              <SkeletonPlaceholder width={272} height={24} />
            )}
          </h2>
          {hasEvent && action && (
            <div className={styles.restaurantEventAction}>{action}</div>
          )}
          {hasEvent &&
            !action &&
            (statusList?.length ? (
              statusList.map((status) => {
                return (
                  <div
                    key={status}
                    className={cx(styles.restaurantEventStatusHeaderItem, {
                      [styles.published]:
                        status === RestaurantEventStatus.Published,
                    })}
                  >
                    <span>{status}</span>
                  </div>
                );
              })
            ) : (
              <div
                className={cx(
                  styles.restaurantEventStatusHeaderItem,
                  styles.published,
                )}
              >
                <span>Published</span>
              </div>
            ))}
        </div>

        <div className={styles.restaurantEventCardDetails}>
          <div className={styles.restaurantEventCardDetailsItem}>
            <p>
              {restaurantEvent?.shift?.endDate
                ? toShortMonthNameDayFormat(restaurantEvent.shift.endDate)
                : SkeletonPlaceholder({ width: 80, height: 20 })}
            </p>
            <p className={styles.time}>
              {eventTime
                ? eventTime
                : SkeletonPlaceholder({ width: 48, height: 16 })}
            </p>
          </div>
          <div
            className={cx(
              styles.restaurantEventCardDetailsItem,
              styles.alignRight,
            )}
          >
            <p>
              {restaurantEvent?.id
                ? restaurantName
                : SkeletonPlaceholder({ width: 100, height: 20 })}
            </p>
          </div>
        </div>
      </div>
      {showDescription &&
        (restaurantEvent?.description != null &&
        restaurantEvent.description !== '' ? (
          <div className={styles.restaurantEventCardContent}>
            <div className={styles.restaurantEventDescription}>
              {restaurantEvent.description}
            </div>
          </div>
        ) : !hasEvent ? (
          <div className={styles.restaurantEventCardContent}>
            <div className={styles.restaurantEventDescription}>
              {SkeletonPlaceholder({ width: 100, height: 16, count: 2 })}
            </div>
          </div>
        ) : null)}
    </>
  );
};
